"use client";

import { createElement, useEffect, useLayoutEffect, useState } from "react";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { readSession } from "@/lib/session";
import { openDonateModal } from "@/lib/donate";
import PhoneLink from "@/components/PhoneLink";

export type UserRole = "guest" | "member" | "committee" | "admin" | "superadmin";

type SiteHeaderProps = {
  userRole?: UserRole;
  fullName?: string;
};

const saffron = "var(--primary-saffron)";

function linkColor(isCurrent: boolean, idle: string) {
  return isCurrent ? saffron : idle;
}

function MenuLink({
  href,
  className,
  style,
  children,
}: {
  href: string;
  className?: string;
  style?: React.CSSProperties;
  children: React.ReactNode;
}) {
  return (
    <Link href={href} prefetch={true} className={className} style={style}>
      {children}
    </Link>
  );
}

function sessionFromCookie(): { role: UserRole; fullName: string } | null {
  const session = readSession();
  if (!session) return null;
  return { role: session.role, fullName: session.full_name };
}

function endSession(event: { preventDefault: () => void }) {
  event.preventDefault();
  window.localStorage.removeItem("token");
  fetch("http://localhost:5002/api/auth/logout", { method: "POST", credentials: "include" }).finally(() => {
    window.location.replace("/login");
  });
}

export default function SiteHeader({
  userRole = "guest",
  fullName,
}: SiteHeaderProps) {
  const pathname = usePathname();
  const [hydrated, setHydrated] = useState(false);
  const [activeRole, setActiveRole] = useState<UserRole>(userRole);
  const [sessionName, setSessionName] = useState(fullName || "");
  const [updates, setUpdates] = useState<string[]>([]);

  useEffect(() => {
    setHydrated(true);
  }, []);

  // Avoid SSR/client pathname mismatch (hydration) — mark active only after mount.
  function isCurrent(path: string) {
    if (!hydrated) return false;
    if (path === "/") return pathname === "/";
    return pathname === path;
  }

  const isHome = isCurrent("/");

  useLayoutEffect(() => {
    const session = sessionFromCookie();
    if (!session) {
      setActiveRole(userRole);
      setSessionName(fullName || "");
      return;
    }
    setActiveRole(session.role);
    const named = (fullName || session.fullName || "").trim();
    if (named) setSessionName(named);
  }, [userRole, fullName, pathname]);

  useEffect(() => {
    if (activeRole !== "member" && activeRole !== "admin") return;
    if (sessionName.trim()) return;
    if (!readSession()) return;

    let cancelled = false;
    fetch("http://localhost:5002/api/profile", { credentials: "include" })
      .then((response) => (response.ok ? response.json() : null))
      .then((data) => {
        if (cancelled || !data?.user) return;
        const name = String(data.user.full_name || data.user.username || "").trim();
        if (name) setSessionName(name);
      })
      .catch(() => {});

    return () => {
      cancelled = true;
    };
  }, [activeRole, sessionName]);

  useEffect(() => {
    let cancelled = false;
    function loadUpdates() {
      fetch("http://localhost:5002/api/updates")
        .then((response) => (response.ok ? response.json() : null))
        .then((data) => {
          if (cancelled || !data?.success) return;
          const lines = Array.isArray(data.updates)
            ? data.updates.map((item: { message?: string }) => String(item.message || "").trim()).filter(Boolean)
            : [];
          setUpdates(lines);
        })
        .catch(() => {});
    }
    loadUpdates();
    window.addEventListener("avt-updates-changed", loadUpdates);
    return () => {
      cancelled = true;
      window.removeEventListener("avt-updates-changed", loadUpdates);
    };
  }, [pathname]);

  useEffect(() => {
    const head = document.querySelector(".site-sticky-head") as HTMLElement | null;
    if (!head) return;

    function syncChromeHeight() {
      const height = Math.ceil(head.getBoundingClientRect().height);
      if (height > 0) {
        document.documentElement.style.setProperty("--site-chrome", `${height}px`);
      }
    }

    syncChromeHeight();
    const observer = new ResizeObserver(syncChromeHeight);
    observer.observe(head);
    window.addEventListener("resize", syncChromeHeight);
    return () => {
      observer.disconnect();
      window.removeEventListener("resize", syncChromeHeight);
    };
  }, [pathname, updates.length]);

  useEffect(() => {
    const hamburgerBtn = document.getElementById("hamburgerClickBtn");
    const mobileMenu = document.getElementById("mainNavMenu");
    const closeMenuBtn = document.getElementById("closeMenuBtn");

    function toggleMenu(isOpen: boolean) {
      if (!mobileMenu) return;

      if (isOpen) {
        mobileMenu.classList.add("open");
        document.body.classList.add("nav-open");
        if (hamburgerBtn) hamburgerBtn.style.display = "none";
        document.body.style.setProperty("overflow", "hidden", "important");
        document.documentElement.style.setProperty("overflow", "hidden", "important");
      } else {
        mobileMenu.classList.remove("open");
        document.body.classList.remove("nav-open");
        if (hamburgerBtn) hamburgerBtn.style.display = "block";
        document.body.style.removeProperty("overflow");
        document.documentElement.style.removeProperty("overflow");
      }
    }

    function onHamburgerClick(event: Event) {
      event.stopPropagation();
      toggleMenu(true);
    }

    function onCloseClick(event: Event) {
      event.stopPropagation();
      toggleMenu(false);
    }

    function onDocumentClick(event: MouseEvent) {
      if (!mobileMenu || !mobileMenu.classList.contains("open")) return;
      const target = event.target as Node;
      if (!mobileMenu.contains(target) && target !== hamburgerBtn) {
        toggleMenu(false);
      }
    }

    function onResize() {
      if (window.innerWidth >= 992) {
        if (mobileMenu) {
          mobileMenu.classList.remove("open");
          mobileMenu.style.transform = "";
        }
        if (hamburgerBtn) hamburgerBtn.style.display = "none";
      } else if (hamburgerBtn && mobileMenu && !mobileMenu.classList.contains("open")) {
        hamburgerBtn.style.display = "block";
      }
    }

    const navLinks = mobileMenu ? Array.from(mobileMenu.querySelectorAll("a")) : [];
    const onNavClick = () => toggleMenu(false);

    hamburgerBtn?.addEventListener("click", onHamburgerClick);
    closeMenuBtn?.addEventListener("click", onCloseClick);
    document.addEventListener("click", onDocumentClick);
    window.addEventListener("resize", onResize);
    navLinks.forEach((link) => link.addEventListener("click", onNavClick));

    return () => {
      hamburgerBtn?.removeEventListener("click", onHamburgerClick);
      closeMenuBtn?.removeEventListener("click", onCloseClick);
      document.removeEventListener("click", onDocumentClick);
      window.removeEventListener("resize", onResize);
      navLinks.forEach((link) => link.removeEventListener("click", onNavClick));
      document.body.classList.remove("nav-open");
      document.body.style.removeProperty("overflow");
      document.documentElement.style.removeProperty("overflow");
    };
  }, [activeRole, pathname]);

  function openDonate(event: React.MouseEvent<HTMLButtonElement>) {
    event.preventDefault();
    event.stopPropagation();
    event.currentTarget.blur();
    openDonateModal();
  }

  return (
    <>
      <div className="site-sticky-head">
      <div className="top-bar">
        <div className="container top-bar-content">
          <div className="contact-info">
            <span>
              📞{" "}
              <PhoneLink phone="9324261020">+91 9324261020</PhoneLink>
              {"\u2002"}||{"\u2002"}
              <PhoneLink phone="7208093020">+91 7208093020</PhoneLink>{" "}
            </span>
          </div>
          <div className="quick-links">
            <button type="button" className="donate-now-btn" onClick={openDonate}>
              Donate Now
            </button>
          </div>
        </div>
      </div>

      <header className="navbar">
        <div className="nav-container">
          <Link href="/" className="logo">
            <img src="/assets/Common/Website_Logo.png" alt="AVT Logo" />

            <div className="logo-text-wrapper">
              <div className="logo-text">
                AVT <span>KALAMBOLI</span>
              </div>
              <div className="logo-subtext">Agroha Vikas Trust</div>
            </div>
          </Link>

          <button className="hamburger-menu-trigger" id="hamburgerClickBtn">
            ☰
          </button>

          <nav className="nav-links" id="mainNavMenu">
            <span className="close-menu" id="closeMenuBtn">
              &times;
            </span>

            {activeRole === "superadmin" ? (
              <>
                <MenuLink href="/super" style={{ fontWeight: 600, color: linkColor(isCurrent("/super"), "#555555") }}>
                  Super Admin
                </MenuLink>
                <MenuLink
                  href="/super/inquiries"
                  style={{ fontWeight: 600, color: linkColor(isCurrent("/super/inquiries"), "#555555") }}
                >
                  Inquiries
                </MenuLink>
                <MenuLink href="/approvals" style={{ fontWeight: 600, color: linkColor(isCurrent("/approvals"), "#555555") }}>
                  Member Approvals
                </MenuLink>
                <MenuLink href="/donations" style={{ fontWeight: 600, color: linkColor(isCurrent("/donations"), "#555555") }}>
                  Donation Records
                </MenuLink>
                <MenuLink
                  href="/donation-purposes"
                  style={{ fontWeight: 600, color: linkColor(isCurrent("/donation-purposes"), "#555555") }}
                >
                  Donation Purposes
                </MenuLink>
                <MenuLink href="/events" style={{ fontWeight: 600, color: linkColor(isCurrent("/events"), "#555555") }}>
                  Event Management
                </MenuLink>
                <MenuLink href="/updates" style={{ fontWeight: 600, color: linkColor(isCurrent("/updates"), "#555555") }}>
                  Latest Updates
                </MenuLink>
                <MenuLink href="/expenses" style={{ fontWeight: 600, color: linkColor(isCurrent("/expenses"), "#555555") }}>
                  Expense Records
                </MenuLink>
                <MenuLink
                  href="/balance-sheet"
                  style={{ fontWeight: 600, color: linkColor(isCurrent("/balance-sheet"), "#555555") }}
                >
                  Balance Sheet
                </MenuLink>
                <Link
                  href="/logout"
                  className="btn-primary"
                  style={{ backgroundColor: "#E65100", borderColor: "#E65100" }}
                  onClick={endSession}
                >
                  Logout
                </Link>
              </>
            ) : activeRole === "admin" ? (
              <>
                <span
                  style={{
                    color: "#080808ff",
                    fontWeight: 600,
                    fontSize: "15px",
                    marginLeft: "15px",
                    whiteSpace: "nowrap",
                  }}
                >
                  Welcome, {sessionName || fullName || "admin"} Ji
                </span>

                <MenuLink
                  href="/donations"
                  style={{
                    fontWeight: 600,
                    color: linkColor(isCurrent("/donations"), "#555555"),
                  }}
                >
                  Donation Records
                </MenuLink>

                <MenuLink
                  href="/donation-purposes"
                  style={{
                    fontWeight: 600,
                    color: linkColor(isCurrent("/donation-purposes"), "#555555"),
                  }}
                >
                  Donation Purposes
                </MenuLink>

                <MenuLink
                  href="/approvals"
                  style={{
                    fontWeight: 600,
                    color: linkColor(isCurrent("/approvals"), "#555555"),
                  }}
                >
                  Member Approvals
                </MenuLink>

                <MenuLink
                  href="/events"
                  style={{
                    fontWeight: 600,
                    color: linkColor(isCurrent("/events"), "#555555"),
                  }}
                >
                  Event Management
                </MenuLink>
                <MenuLink
                  href="/updates"
                  style={{
                    fontWeight: 600,
                    color: linkColor(isCurrent("/updates"), "#555555"),
                  }}
                >
                  Latest Updates
                </MenuLink>
                <MenuLink
                  href="/expenses"
                  style={{
                    fontWeight: 600,
                    color: linkColor(isCurrent("/expenses"), "#555555"),
                  }}
                >
                  Expense Records
                </MenuLink>
                <MenuLink
                  href="/balance-sheet"
                  style={{
                    fontWeight: 600,
                    color: linkColor(isCurrent("/balance-sheet"), "#555555"),
                  }}
                >
                  Balance Sheet
                </MenuLink>
                <MenuLink
                  href="/profile"
                  style={{
                    fontWeight: 600,
                    color: linkColor(isCurrent("/profile"), "#555555"),
                  }}
                >
                  Admin Profile
                </MenuLink>

                <Link
                  href="/logout"
                  className="btn-primary"
                  style={{ backgroundColor: "#E65100", borderColor: "#E65100" }}
                  onClick={endSession}
                >
                  Logout
                </Link>
              </>
            ) : activeRole === "member" || activeRole === "committee" ? (
              <>
                <span
                  style={{
                    color: "#000000ff",
                    fontWeight: 600,
                    fontSize: "15px",
                    marginLeft: "15px",
                    whiteSpace: "nowrap",
                  }}
                >
                  Welcome {sessionName || fullName || "Member"} Ji
                </span>

                <Link href="/" className={isHome ? "active" : undefined} style={{ color: linkColor(isHome, "#333333") }}>
                  Home
                </Link>
                <Link
                  href="/about"
                  className={isCurrent("/about") ? "active" : undefined}
                  style={{ color: linkColor(isCurrent("/about"), "#333333") }}
                >
                  About Us
                </Link>
                <Link
                  href="/committees"
                  className={isCurrent("/committees") ? "active" : undefined}
                  style={{ color: linkColor(isCurrent("/committees"), "#333333") }}
                >
                  Committees
                </Link>
                <Link
                  href="/directory"
                  className={isCurrent("/directory") ? "active" : undefined}
                  style={{ color: linkColor(isCurrent("/directory"), "#333333") }}
                >
                  Directory
                </Link>
                <Link
                  href="/gallery"
                  className={isCurrent("/gallery") ? "active" : undefined}
                  style={{ color: linkColor(isCurrent("/gallery"), "#333333") }}
                >
                  Gallery
                </Link>
                <Link
                  href="/contact"
                  className={isCurrent("/contact") ? "active" : undefined}
                  style={{ color: linkColor(isCurrent("/contact"), "#333333") }}
                >
                  Contact Us
                </Link>
                <Link
                  href="/profile"
                  className="btn-primary"
                  style={{ backgroundColor: "#333", borderColor: "#333" }}
                >
                  My Profile
                </Link>
                <Link
                  href="/logout"
                  className="btn-primary"
                  style={{ backgroundColor: "#E65100", borderColor: "#E65100" }}
                  onClick={endSession}
                >
                  Logout
                </Link>
              </>
            ) : (
              <>
                <Link href="/" className={isHome ? "active" : undefined} style={{ color: linkColor(isHome, "#333333") }}>
                  Home
                </Link>
                <Link
                  href="/about"
                  className={isCurrent("/about") ? "active" : undefined}
                  style={{ color: linkColor(isCurrent("/about"), "#333333") }}
                >
                  About Us
                </Link>
                <Link
                  href="/committees"
                  className={isCurrent("/committees") ? "active" : undefined}
                  style={{ color: linkColor(isCurrent("/committees"), "#333333") }}
                >
                  Committees
                </Link>
                <Link
                  href="/gallery"
                  className={isCurrent("/gallery") ? "active" : undefined}
                  style={{ color: linkColor(isCurrent("/gallery"), "#333333") }}
                >
                  Gallery
                </Link>
                <Link
                  href="/contact"
                  className={isCurrent("/contact") ? "active" : undefined}
                  style={{ color: linkColor(isCurrent("/contact"), "#333333") }}
                >
                  Contact Us
                </Link>
                <Link href="/register" className="btn-primary">
                  Become a Member
                </Link>
                <Link href="/login" className="btn-primary">
                  Login
                </Link>
              </>
            )}
          </nav>
        </div>
      </header>

      <div className="news-ticker">
        <div className="ticker-label">Latest Updates</div>
        <div className="ticker-content">
          {createElement(
            "marquee",
            {
              behavior: "scroll",
              direction: "left",
              scrollamount: "6",
            },
            updates.join("    •    ")
          )}
        </div>
      </div>
      </div>

      {(activeRole === "admin" || activeRole === "superadmin" || activeRole === "member") && (
        <div id="sessionTimeoutModal" style={{ display: "none" }}>
          <div className="session-overlay"></div>
          <div className="session-popup">
            <h2>⚠ Session Expiring Soon</h2>
            <p>Aapka session expire hone wala hai.</p>
            <h3 id="sessionCountdown">05:00</h3>
            <div className="session-actions">
              <button id="continueSessionBtn">Continue Session</button>
              <button id="logoutSessionBtn" type="button" onClick={endSession}>
                Logout
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
